import { computed, onBeforeUnmount, onMounted, ref, shallowRef, type CSSProperties } from "vue";

/** The visible rectangle, including keyboard-induced panning on mobile Safari. */
export function useVisualViewport() {
  const isTouch = ref(false);
  const bounds = shallowRef({
    width: window.innerWidth,
    height: window.innerHeight,
    top: 0,
    left: 0,
  });
  let frame = 0;
  let settleTimers: ReturnType<typeof setTimeout>[] = [];

  function refresh() {
    const viewport = window.visualViewport;
    // Pinch zoom must magnify the page, rather than resize/reflow the terminal.
    if (viewport && Math.abs(viewport.scale - 1) > 0.01) return;
    const next = {
      width: viewport?.width ?? window.innerWidth,
      height: viewport?.height ?? window.innerHeight,
      top: viewport?.offsetTop ?? 0,
      left: viewport?.offsetLeft ?? 0,
    };
    if (next.width <= 0 || next.height <= 0) return;
    if (
      Object.entries(next).some(([key, value]) => bounds.value[key as keyof typeof next] !== value)
    ) {
      bounds.value = next;
    }
  }

  function schedule() {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(refresh);
  }

  function settle() {
    schedule();
    settleTimers.forEach(clearTimeout);
    // Orientation/keyboard animations can finish after the first resize event.
    settleTimers = [100, 300, 700].map((delay) => setTimeout(schedule, delay));
  }

  onMounted(() => {
    isTouch.value = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    refresh();
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", settle);
    window.addEventListener("pageshow", settle);
    document.addEventListener("visibilitychange", settle);
  });

  onBeforeUnmount(() => {
    cancelAnimationFrame(frame);
    settleTimers.forEach(clearTimeout);
    window.visualViewport?.removeEventListener("resize", schedule);
    window.visualViewport?.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
    window.removeEventListener("orientationchange", settle);
    window.removeEventListener("pageshow", settle);
    document.removeEventListener("visibilitychange", settle);
  });

  const fixedStyle = computed<CSSProperties>(() => ({
    position: "fixed",
    top: `${bounds.value.top}px`,
    left: `${bounds.value.left}px`,
    width: `${bounds.value.width}px`,
    height: `${bounds.value.height}px`,
    right: "auto",
    bottom: "auto",
  }));

  return { isTouch, bounds, fixedStyle, refresh };
}
